import React, { useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { AuthService } from '@/services/auth';
import { useAuth } from '@/contexts/AuthContext';
import { isLinkedInConfigured, startLinkedInSignIn } from '@/services/socialAuth';
import { SocialAuthButtons } from './SocialAuthButtons';
import './SignupForm.css';
import logoSrc from '../../assets/logo.webp'

import { isPasswordValid, PASSWORD_REQUIREMENT_MESSAGE } from '@/utils/passwordPolicy';

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY ?? '0x4AAAAAAERmL7xQRSufCoQ6';

type TurnstileInstance = {
  render: (
    container: HTMLElement,
    options: {
      sitekey: string;
      callback: (token: string) => void;
      'expired-callback'?: () => void;
      'error-callback'?: () => void;
      theme?: 'light' | 'dark' | 'auto';
    },
  ) => number;
  reset: (widgetId?: number) => void;
  remove: (widgetId?: number) => void;
};

declare global {
  interface Window {
    turnstile?: TurnstileInstance;
  }
}

const COUNTRY_CODE_DATA = `
Afghanistan|93
Albania|355
Algeria|213
American Samoa|1-684
Andorra|376
Angola|244
Anguilla|1-264
Antarctica|672
Antigua and Barbuda|1-268
Argentina|54
Armenia|374
Aruba|297
Australia|61
Austria|43
Azerbaijan|994
Bahamas|1-242
Bahrain|973
Bangladesh|880
Barbados|1-246
Belarus|375
Belgium|32
Belize|501
Benin|229
Bermuda|1-441
Bhutan|975
Bolivia|591
Bosnia and Herzegovina|387
Botswana|267
Brazil|55
British Indian Ocean Territory|246
British Virgin Islands|1-284
Brunei|673
Bulgaria|359
Burkina Faso|226
Burundi|257
Cambodia|855
Cameroon|237
Cape Verde|238
Cayman Islands|1-345
Central African Republic|236
Chad|235
Chile|56
China|86
Christmas Island|61
Cocos Islands|61
Colombia|57
Comoros|269
Cook Islands|682
Costa Rica|506
Croatia|385
Cuba|53
Curacao|599
Cyprus|357
Czech Republic|420
Democratic Republic of the Congo|243
Denmark|45
Djibouti|253
Dominica|1-767
Dominican Republic|1-809, 1-829, 1-849
East Timor|670
Ecuador|593
Egypt|20
El Salvador|503
Equatorial Guinea|240
Eritrea|291
Estonia|372
Ethiopia|251
Falkland Islands|500
Faroe Islands|298
Fiji|679
Finland|358
France|33
French Polynesia|689
Gabon|241
Gambia|220
Georgia|995
Germany|49
Ghana|233
Gibraltar|350
Greece|30
Greenland|299
Grenada|1-473
Guam|1-671
Guatemala|502
Guernsey|44-1481
Guinea|224
Guinea-Bissau|245
Guyana|592
Haiti|509
Honduras|504
Hong Kong|852
Hungary|36
Iceland|354
India|91
Indonesia|62
Iran|98
Iraq|964
Ireland|353
Isle of Man|44-1624
Israel|972
Italy|39
Ivory Coast|225
Jamaica|1-876
Japan|81
Jersey|44-1534
Jordan|962
Kazakhstan|7
Kenya|254
Kiribati|686
Kosovo|383
Kuwait|965
Kyrgyzstan|996
Laos|856
Latvia|371
Lebanon|961
Lesotho|266
Liberia|231
Libya|218
Liechtenstein|423
Lithuania|370
Luxembourg|352
Macao|853
Macedonia|389
Madagascar|261
Malawi|265
Malaysia|60
Maldives|960
Mali|223
Malta|356
Marshall Islands|692
Mauritania|222
Mauritius|230
Mayotte|262
Mexico|52
Micronesia|691
Moldova|373
Monaco|377
Mongolia|976
Montenegro|382
Montserrat|1-664
Morocco|212
Mozambique|258
Myanmar|95
Namibia|264
Nauru|674
Nepal|977
Netherlands|31
Netherlands Antilles|599
New Caledonia|687
New Zealand|64
Nicaragua|505
Niger|227
Nigeria|234
Niue|683
North Korea|850
Northern Mariana Islands|1-670
Norway|47
Oman|968
Pakistan|92
Palau|680
Palestine|970
Panama|507
Papua New Guinea|675
Paraguay|595
Peru|51
Philippines|63
Pitcairn|64
Poland|48
Portugal|351
Puerto Rico|1-787, 1-939
Qatar|974
Republic of the Congo|242
Reunion|262
Romania|40
Russia|7
Rwanda|250
Saint Barthelemy|590
Saint Helena|290
Saint Kitts and Nevis|1-869
Saint Lucia|1-758
Saint Martin|590
Saint Pierre and Miquelon|508
Saint Vincent and the Grenadines|1-784
Samoa|685
San Marino|378
Sao Tome and Principe|239
Saudi Arabia|966
Senegal|221
Serbia|381
Seychelles|248
Sierra Leone|232
Singapore|65
Sint Maarten|1-721
Slovakia|421
Slovenia|386
Solomon Islands|677
Somalia|252
South Africa|27
South Korea|82
South Sudan|211
Spain|34
Sri Lanka|94
Sudan|249
Suriname|597
Svalbard and Jan Mayen|47
Swaziland|268
Sweden|46
Switzerland|41
Syria|963
Taiwan|886
Tajikistan|992
Tanzania|255
Thailand|66
Togo|228
Tokelau|690
Tonga|676
Trinidad and Tobago|1-868
Tunisia|216
Turkey|90
Turkmenistan|993
Turks and Caicos Islands|1-649
Tuvalu|688
U.S. Virgin Islands|1-340
Uganda|256
Ukraine|380
United Arab Emirates|971
United Kingdom|44
United States|1
Uruguay|598
Uzbekistan|998
Vanuatu|678
Vatican|379
Venezuela|58
Vietnam|84
Wallis and Futuna|681
Western Sahara|212
Yemen|967
Zambia|260
Zimbabwe|263
`.trim();

const COUNTRY_CODES = COUNTRY_CODE_DATA.split('\n')
  .map((line) => {
    const [name, rawCode] = line.split('|');
    return {
      name,
      code: `+${rawCode.split(',')[0].trim().replace(/[^0-9]/g, '')}`,
    };
  })
  .sort((a, b) => a.name.localeCompare(b.name));

interface FormData {
  email: string;
  company_name: string;
  first_name: string;
  last_name: string;
  phone_country_code: string;
  phone_number: string;
  password: string;
  confirm_password: string;
}

interface FieldErrors {
  email?: string;
  company_name?: string;
  first_name?: string;
  last_name?: string;
  phone_number?: string;
  password?: string;
  confirm_password?: string;
}

const EyeIcon = ({ off = false }: { off?: boolean }) => off ? (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
) : (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="10" />
    <polyline points="8 12.5 11 15.5 16 9.5" />
  </svg>
);

const SparkIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
);

export const SignupForm: React.FC = () => {
  const navigate = useNavigate();
  const { loginWithSocial } = useAuth();
  const linkedInAutoStartRef = useRef(false);
  const [form, setForm] = useState<FormData>({
    email: '',
    company_name: '',
    first_name: '',
    last_name: '',
    phone_country_code: '+1',
    phone_number: '',
    password: '',
    confirm_password: '',
  });
  const [errors, setErrors] = useState<FieldErrors>({});
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileError, setTurnstileError] = useState('');
  const [isTurnstileRendered, setIsTurnstileRendered] = useState(false);
  const turnstileRef = useRef<HTMLDivElement | null>(null);
  const turnstileWidgetId = useRef<number | null>(null);

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY || !turnstileRef.current) return;

    let cancelled = false;
    const scriptId = 'turnstile-api-script';

    const renderTurnstile = () => {
      if (cancelled || !turnstileRef.current || !window.turnstile) return;

      try {
        turnstileWidgetId.current = window.turnstile.render(turnstileRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          theme: 'light',
          callback: token => {
            setTurnstileToken(token);
            setTurnstileError('');
          },
          'expired-callback': () => {
            setTurnstileToken('');
            setTurnstileError('The security check expired. Please complete it again.');
          },
          'error-callback': () => {
            setTurnstileToken('');
            setTurnstileError('The security check could not be loaded. Please try again.');
          },
        });
        setIsTurnstileRendered(true);
      } catch {
        setTurnstileError('The security check could not be loaded. Please refresh the page.');
      }
    };

    if (window.turnstile?.render) {
      renderTurnstile();
    } else {
      let script = document.getElementById(scriptId) as HTMLScriptElement | null;
      const onLoad = () => {
        script?.setAttribute('data-loaded', 'true');
        renderTurnstile();
      };

      if (!script) {
        script = document.createElement('script');
        script.id = scriptId;
        script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
        script.async = true;
        script.defer = true;
        script.addEventListener('load', onLoad, { once: true });
        script.addEventListener('error', () => {
          setTurnstileError('The security check could not be loaded. Please refresh the page.');
        }, { once: true });
        document.head.appendChild(script);
      } else if (script.dataset.loaded === 'true') {
        renderTurnstile();
      } else {
        script.addEventListener('load', onLoad, { once: true });
      }

      return () => {
        cancelled = true;
        script?.removeEventListener('load', onLoad);
        if (window.turnstile && turnstileWidgetId.current !== null) {
          window.turnstile.remove(turnstileWidgetId.current);
          turnstileWidgetId.current = null;
        }
      };
    }

    return () => {
      cancelled = true;
      if (window.turnstile && turnstileWidgetId.current !== null) {
        window.turnstile.remove(turnstileWidgetId.current);
        turnstileWidgetId.current = null;
      }
    };
  }, []);

  const resetTurnstile = () => {
    setTurnstileToken('');
    if (window.turnstile && turnstileWidgetId.current !== null) {
      window.turnstile.reset(turnstileWidgetId.current);
    }
  };

  // ── Social sign-in (Google / LinkedIn) ───────────────────────────────────

  // Arriving as /signup?social=linkedin (hand-off from adgptise.com): start the flow.
  useEffect(() => {
    if (linkedInAutoStartRef.current) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('social') !== 'linkedin' || !isLinkedInConfigured()) return;
    linkedInAutoStartRef.current = true;
    window.history.replaceState({}, document.title, window.location.pathname);
    startLinkedInSignIn();
  }, []);

  const handleSocialResult = (result: Awaited<ReturnType<typeof loginWithSocial>>) => {
    if (!result.success) {
      setSubmitError(result.error ?? 'Sign-up failed. Please try again.');
      setIsSubmitting(false);
      return;
    }

    if (result.requires_2fa && result.temp_token) {
      navigate('/login', { replace: true, state: { tempToken: result.temp_token } });
      return;
    }

    const currentUser = AuthService.getUser();
    navigate(currentUser?.role === 'staff' ? '/dashboard/domains' : '/dashboard', { replace: true });
  };

  const handleGoogleCredential = (credential: string) => {
    setIsSubmitting(true);
    setSubmitError('');
    loginWithSocial('google', credential)
      .then(handleSocialResult)
      .catch(() => {
        setSubmitError('An unexpected error occurred');
        setIsSubmitting(false);
      });
  };

  const set = (field: keyof FormData) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }));
    if (errors[field as keyof FieldErrors]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
    if (submitError) setSubmitError('');
  };

  const validate = (): boolean => {
    const e: FieldErrors = {};

    if (!form.email.trim()) e.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = 'Enter a valid email address';

    if (!form.company_name.trim()) e.company_name = 'Publication name is required';
    if (!form.first_name.trim())   e.first_name   = 'First name is required';
    if (!form.last_name.trim())    e.last_name    = 'Last name is required';

    if (!form.phone_number.trim())
      e.phone_number = 'Phone number is required';
    else if (!/^\d{4,15}$/.test(form.phone_number.replace(/[\s\-().]/g, '')))
      e.phone_number = 'Enter a valid phone number';

    if (!form.password)
      e.password = 'Password is required';
    else if (!isPasswordValid(form.password))
      e.password = PASSWORD_REQUIREMENT_MESSAGE;

    if (!form.confirm_password)
      e.confirm_password = 'Please confirm your password';
    else if (form.password !== form.confirm_password)
      e.confirm_password = 'Passwords do not match';

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    if (TURNSTILE_SITE_KEY && !turnstileToken) {
      setSubmitError('Please complete the security check before continuing.');
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      const response = await AuthService.register({
        email:              form.email.trim(),
        company_name:       form.company_name.trim(),
        first_name:         form.first_name.trim(),
        last_name:          form.last_name.trim(),
        phone_country_code: form.phone_country_code,
        phone_number:       form.phone_number.trim(),
        password:           form.password,
        turnstile_token:    turnstileToken || undefined,
      });

      if (response.success) {
        setIsSuccess(true);
      } else {
        if (response.error?.code === 'CONFLICT') {
          setErrors(current => ({ ...current, email: 'An account with this email address already exists' }));
        } else {
          setSubmitError(response.error?.message || 'Registration failed. Please try again.');
        }
        resetTurnstile();
      }
    } catch {
      setSubmitError('An unexpected error occurred. Please try again.');
      resetTurnstile();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="dashboard-signup">
      <header className="signup-header">
        <a className="signup-brand" href="https://adgptise.com/home" aria-label="adGPTise home">
           <img src={logoSrc} alt="adGPTise" width="300" height="80" fetchPriority="high" />
            <span>BETA</span>
        </a>
        <p>Already have an account? <RouterLink to="/login">Sign in <span>→</span></RouterLink></p>
      </header>

      <main className="signup-main">
        <div className="signup-layout">
          <aside className="signup-promise">
            <p className="signup-eyebrow">FOR DIGITAL PUBLISHERS</p>
            <h1>Create Your Free <span>Publisher Account</span></h1>
            <p>Create your free publisher account. Verify your email, then follow the activation step shown in your confirmation.</p>

            <div className="signup-journey" aria-label="Publisher onboarding steps">
              <article className="current"><span>1</span><div><b>Create your account</b><small>Tell us who you are and which publication you represent.</small></div></article>
              <article><span>2</span><div><b>Verify your work email</b><small>Use the secure link sent to your inbox.</small></div></article>
              <article><span>3</span><div><b>Connect your publication</b><small>Follow the dashboard guide once your account is active.</small></div></article>
            </div>

            <div className="signup-proof">
              <div><SparkIcon /></div>
              <p><strong>Start with intelligence.</strong>Add AI summaries, page-level insights and contextual monetization when you are ready.</p>
            </div>
          </aside>

          <section className="signup-card" aria-labelledby="signup-title">
            {isSuccess ? (
              <div className="signup-success">
                <div className="signup-success-icon"><CheckIcon /></div>
                <span className="signup-pill">ONE MORE STEP</span>
                <h2 id="signup-title">Check your inbox</h2>
                <p>We sent a verification link to <strong>{form.email.trim()}</strong>. Verify your email to see whether your account is active or pending approval.</p>
                <div className="signup-next"><span>NEXT</span><b>Verify email → Follow your activation status → Begin setup</b></div>
              </div>
            ) : (
              <>
                <div className="signup-card-heading">
                  <span className="signup-pill">PUBLISHER ACCOUNT</span>
                  <h2 id="signup-title">Create your free account</h2>
                  <p>No credit card. No website rebuild. Setup takes only a few minutes, starting with email verification.</p>
                </div>

                {submitError && <div className="signup-alert" role="alert"><span>!</span><p>{submitError}</p></div>}

                <form className="signup-form" onSubmit={handleSubmit} noValidate>
                  <div className="signup-two-column">
                    <div className={`signup-field ${errors.first_name ? 'has-error' : ''}`}>
                      <label htmlFor="signup-first-name">First name</label>
                      <input id="signup-first-name" value={form.first_name} onChange={set('first_name')} disabled={isSubmitting} autoComplete="given-name" placeholder="Jane" aria-invalid={Boolean(errors.first_name)} required />
                      <small className="signup-error">{errors.first_name}</small>
                    </div>
                    <div className={`signup-field ${errors.last_name ? 'has-error' : ''}`}>
                      <label htmlFor="signup-last-name">Last name</label>
                      <input id="signup-last-name" value={form.last_name} onChange={set('last_name')} disabled={isSubmitting} autoComplete="family-name" placeholder="Doe" aria-invalid={Boolean(errors.last_name)} required />
                      <small className="signup-error">{errors.last_name}</small>
                    </div>
                  </div>

                  <div className={`signup-field ${errors.email ? 'has-error' : ''}`}>
                    <label htmlFor="signup-email">Work email</label>
                    <input id="signup-email" type="email" value={form.email} onChange={set('email')} disabled={isSubmitting} autoComplete="email" placeholder="you@publication.com" aria-invalid={Boolean(errors.email)} autoFocus required />
                    <small className="signup-hint">Use the email associated with your publication.</small>
                    <small className="signup-error">{errors.email}</small>
                  </div>

                  <div className={`signup-field ${errors.company_name ? 'has-error' : ''}`}>
                    <label htmlFor="signup-company">Publication name</label>
                    <input id="signup-company" value={form.company_name} onChange={set('company_name')} disabled={isSubmitting} autoComplete="organization" placeholder="Business Today Middle East" aria-invalid={Boolean(errors.company_name)} required />
                    <small className="signup-error">{errors.company_name}</small>
                  </div>

                  <div className={`signup-field ${errors.phone_number ? 'has-error' : ''}`}>
                    <label htmlFor="signup-phone">Publication phone</label>
                    <div className="signup-phone-row">
                      <select aria-label="Country code" value={form.phone_country_code} onChange={set('phone_country_code')} disabled={isSubmitting}>
                        {COUNTRY_CODES.map(({ code, name }) => (
                          <option key={`${code}-${name}`} value={code}>
                            {name} ({code})
                          </option>
                        ))}
                      </select>
                      <input id="signup-phone" type="tel" value={form.phone_number} onChange={set('phone_number')} disabled={isSubmitting} autoComplete="tel-national" placeholder="501234567" aria-invalid={Boolean(errors.phone_number)} required />
                    </div>
                    <small className="signup-error">{errors.phone_number}</small>
                  </div>

                  <div className={`signup-field ${errors.password ? 'has-error' : ''}`}>
                    <label htmlFor="signup-password">Password</label>
                    <div className="signup-password-row">
                      <input id="signup-password" type={showPassword ? 'text' : 'password'} value={form.password} onChange={set('password')} disabled={isSubmitting} autoComplete="new-password" placeholder="Minimum 8 characters" aria-invalid={Boolean(errors.password)} required />
                      <button type="button" onClick={() => setShowPassword(value => !value)} aria-label={showPassword ? 'Hide password' : 'Show password'}><EyeIcon off={showPassword} /></button>
                    </div>
                    <small className="signup-hint">{PASSWORD_REQUIREMENT_MESSAGE}</small>
                    <small className="signup-error">{errors.password}</small>
                  </div>

                  <div className={`signup-field ${errors.confirm_password ? 'has-error' : ''}`}>
                    <label htmlFor="signup-confirm-password">Confirm password</label>
                    <div className="signup-password-row">
                      <input id="signup-confirm-password" type={showConfirm ? 'text' : 'password'} value={form.confirm_password} onChange={set('confirm_password')} disabled={isSubmitting} autoComplete="new-password" placeholder="Re-enter your password" aria-invalid={Boolean(errors.confirm_password)} required />
                      <button type="button" onClick={() => setShowConfirm(value => !value)} aria-label={showConfirm ? 'Hide password' : 'Show password'}><EyeIcon off={showConfirm} /></button>
                    </div>
                    <small className="signup-error">{errors.confirm_password}</small>
                  </div>

                  <div className={`signup-field signup-turnstile ${turnstileError ? 'has-error' : ''}`}>
                    <label>Security check</label>
                    {TURNSTILE_SITE_KEY ? <div ref={turnstileRef} /> : <div className="signup-warning">Security verification is not configured for this environment.</div>}
                    {!isTurnstileRendered && TURNSTILE_SITE_KEY && !turnstileError && <small className="signup-hint">Loading security check…</small>}
                    <small className="signup-error" role={turnstileError ? 'alert' : undefined}>{turnstileError}</small>
                  </div>

                  <button className="signup-submit" type="submit" aria-label="Submit Registration" disabled={isSubmitting || Boolean(TURNSTILE_SITE_KEY && !turnstileToken)}>
                    {isSubmitting ? 'Creating account…' : 'Create publisher account'} <span>→</span>
                  </button>
                  <SocialAuthButtons onGoogleCredential={handleGoogleCredential} disabled={isSubmitting} />
                  <p className="signup-secure-note"><span>◇</span>Secure registration · Email verification required</p>
                </form>
              </>
            )}
          </section>
        </div>

        <div className="signup-assurances" aria-label="Account assurances">
          <span><b>✓</b>No credit card required</span><span><b>✓</b>Works with your existing CMS</span><span><b>✓</b>Privacy-conscious by design</span><span><b>✓</b>Publisher control retained</span>
        </div>
      </main>
    </div>
  );
};
